import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';
import type { FinCloseRun, FinCloseTask } from '@/types/finance';

export function FinanceCloseCenterPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('FINANCE_MANAGE', 'FINANCE_SETTINGS_MANAGE');
  const [runs, setRuns] = useState<FinCloseRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [tasks, setTasks] = useState<FinCloseTask[]>([]);
  const [closeType, setCloseType] = useState('MONTH');
  const [fiscalYear, setFiscalYear] = useState(String(new Date().getFullYear()));
  const [fiscalPeriod, setFiscalPeriod] = useState(String(new Date().getMonth() + 1));
  const [starting, setStarting] = useState(false);
  const { rows: paginatedTasks, tablePaginationProps: tasksPaginationProps } = useClientPaginatedRows(tasks);

  const loadRuns = () => {
    financeApi.listCloseRuns().then(setRuns).catch((e) => showApiError(e, 'Failed to load close runs'));
  };

  useEffect(() => {
    loadRuns();
  }, []);

  useEffect(() => {
    if (!selectedRunId) {
      setTasks([]);
      return;
    }
    financeApi.listCloseTasks(selectedRunId).then(setTasks).catch((e) => showApiError(e, 'Failed to load tasks'));
  }, [selectedRunId]);

  const startClose = async () => {
    setStarting(true);
    try {
      const run = await financeApi.startCloseRun({
        closeType,
        fiscalYear: Number(fiscalYear),
        fiscalPeriod: Number(fiscalPeriod),
      });
      notify.success(`Close run started (${run.closeType})`);
      setSelectedRunId(run.id);
      loadRuns();
    } catch (e) {
      showApiError(e, 'Failed to start close');
    } finally {
      setStarting(false);
    }
  };

  const completeTask = async (task: FinCloseTask) => {
    if (!selectedRunId) return;
    try {
      await financeApi.completeCloseTask(selectedRunId, task.id, 'Completed from close center');
      notify.success(`Task completed: ${task.taskName}`);
      setTasks(await financeApi.listCloseTasks(selectedRunId));
      loadRuns();
    } catch (e) {
      showApiError(e, 'Failed to complete task');
    }
  };

  return (
    <div>
      <PageHeader
        title="Financial close"
        description="Month, quarter, and year-end close checklist with task owners and dependencies"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Administration', to: '/finance/control' },
          { label: 'Close' },
        ]}
      />

      <div className="card p-4 mb-4 space-y-3">
        <h3 className="font-semibold">Start a new close</h3>
        <div className="grid gap-3 sm:grid-cols-4 items-end">
          <Select
            label="Close type"
            value={closeType}
            onChange={setCloseType}
            options={[
              { value: 'MONTH', label: 'Month end' },
              { value: 'QUARTER', label: 'Quarter end' },
              { value: 'YEAR', label: 'Year end' },
            ]}
          />
          <Input label="Fiscal year" value={fiscalYear} onChange={(e) => setFiscalYear(e.target.value)} />
          <Input label="Period" value={fiscalPeriod} onChange={(e) => setFiscalPeriod(e.target.value)} />
          {canManage && <Button loading={starting} onClick={() => void startClose()}>Start close</Button>}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card p-4">
          <h3 className="font-semibold mb-2">Close runs</h3>
          <ul className="text-sm space-y-2">
            {runs.map((run) => (
              <li key={run.id}>
                <button
                  type="button"
                  className={`w-full text-left rounded border p-2 ${
                    selectedRunId === run.id ? 'border-primary-500 bg-primary-50' : 'border-slate-200'
                  }`}
                  onClick={() => setSelectedRunId(run.id)}
                >
                  <div className="font-medium">{run.closeType} — {run.fiscalYear}-{run.fiscalPeriod}</div>
                  <div className="text-xs text-slate-500">{run.statusCode}</div>
                </button>
              </li>
            ))}
            {runs.length === 0 && <p className="text-slate-500">No close runs yet.</p>}
          </ul>
        </div>

        <div className="card p-4 lg:col-span-2">
          <h3 className="font-semibold mb-3">Close checklist</h3>
          {!selectedRunId ? (
            <p className="text-sm text-slate-500">Select or start a close run to see tasks.</p>
          ) : (
            <>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-2 text-left">Task</th>
                  <th className="p-2 text-left">Owner role</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {paginatedTasks.map((task) => (
                  <tr key={task.id} className="border-t">
                    <td className="p-2">{task.taskName}</td>
                    <td className="p-2 text-xs">{task.ownerRoleCode ?? '—'}</td>
                    <td className="p-2">{task.statusCode}</td>
                    <td className="p-2 text-right">
                      {task.statusCode === 'PENDING' && canManage && (
                        <button
                          type="button"
                          className="text-primary-600 text-xs hover:underline"
                          onClick={() => void completeTask(task)}
                        >
                          Complete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination {...tasksPaginationProps} hasRows={tasks.length > 0} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
